import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  actionAddPick,
  actionMarkPicksSubmitted,
  actionStagePicks,
  actionClearStagedPicks,
} from 'lib/actions';
import {
  findLpInRequirements,
  formatMaterialQty,
} from 'lib/utils/pickingMaterials';
import { getConfirmedRunningStart } from 'lib/selectors/getConfirmedRunningStart';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import usePickingSummary from 'lib/hooks/usePickingSummary';
import useSubmitPicks from 'lib/hooks/useSubmitPicks';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { MANUAL_LP_SELECTION_ENABLED } from 'lib/selectors/getPickingFieldConfig';

import SelectBatchStep from './SelectBatchStep';
import ConfirmQuantitiesStep from './ConfirmQuantitiesStep';

const STEP_SELECT = 1;
const STEP_CONFIRM = 2;

const SCAN_OPTIONS = { minLength: 3 };

const emptySelections = (materials) => {
  return materials.reduce((acc, mat) => {
    return { ...acc, [mat.itemNumber]: new Set() };
  }, {});
};

// Quantities for the confirm step: each selected LP defaults to 0 so the
// operator explicitly enters the amount picked, while any quantity the
// operator already entered is preserved across back-and-forth navigation.
// LPs no longer selected are dropped.
const defaultQuantities = (materials, selections, prevQuantities = {}) => {
  const qtys = {};
  materials.forEach((mat) => {
    const sel = selections[mat.itemNumber] || new Set();
    sel.forEach((lp) => {
      const isAvailable = mat.lps.some((l) => {
        return l.lp === lp;
      });
      if (isAvailable) {
        const key = `${mat.itemNumber}:${lp}`;
        qtys[key] = prevQuantities[key] ?? 0;
      }
    });
  });
  return qtys;
};

const MaterialPickingWizard = ({
  wooId,
  phase,
  mode,
  onComplete,
  onBack,
  onClose,
}) => {
  const dispatch = useDispatch();
  const confirmedStart = useSelector(getConfirmedRunningStart);
  const {
    materials,
    picks: recordedPicks,
    loaded,
    error,
    refetch,
  } = usePickingSummary(wooId);
  const submitPicks = useSubmitPicks();

  const [step, setStep] = useState(STEP_SELECT);
  const [selections, setSelections] = useState(() => {
    return emptySelections(materials);
  });
  const [quantities, setQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // pickId per LP, kept until a submit succeeds or the selection is reset. A
  // retry, even with a changed quantity, reuses the ids, so useSubmitPicks
  // recognizes picks whose write landed instead of appending them again.
  const pickIdsRef = useRef({});

  // A pick is only submittable when at least one selected LP has a quantity
  // above zero — an all-zero confirm step must not produce an empty submit.
  const hasSubmittableQuantity = useMemo(() => {
    return materials.some((mat) => {
      const sel = selections[mat.itemNumber];
      if (!sel || sel.size === 0) return false;
      return [...sel].some((lp) => {
        return (quantities[`${mat.itemNumber}:${lp}`] ?? 0) > 0;
      });
    });
  }, [materials, selections, quantities]);

  // Scan-only: an LP is added to the selection by scanning its barcode. Manual
  // tile selection is disabled (see SelectBatchStep), so the scan handler is the
  // only way to select an LP. Scanning is add-only; re-scanning an LP is a no-op.
  const handleScan = useCallback(
    (scanned) => {
      const code = String(scanned || '').trim();
      // Until the WOO's materials have loaded every LP looks unknown, so say so
      // instead of rejecting the scan.
      if (!loaded) {
        throwErrorToast(
          error
            ? 'Materials could not be loaded. Retry, then scan again.'
            : 'Materials are still loading. Scan again in a moment.'
        );
        return;
      }
      const resolved = findLpInRequirements(materials, code);

      if (!resolved) {
        throwErrorToast(`${code} is not an available LP for this operation.`);
        return;
      }

      setSelections((prev) => {
        const current = new Set(prev[resolved.itemNumber] || []);
        current.add(resolved.lpInfo.lp);
        return { ...prev, [resolved.itemNumber]: current };
      });
      throwSuccessToast(`Added ${resolved.lpInfo.lp}`);
    },
    [materials, loaded, error]
  );

  useBarcodeScanHandler('material-picking-wizard', handleScan, {
    priority: BARCODE_HANDLER_PRIORITIES.PICK_MATERIAL_STEP,
    enabled: step === STEP_SELECT,
    scanOptions: SCAN_OPTIONS,
  });

  // Development-only: allow tapping a tile to select/deselect an LP without a
  // scanner. Gated by MANUAL_LP_SELECTION_ENABLED so deployed builds stay scan-only.
  const handleToggleLp = useCallback((itemNumber, lp) => {
    setSelections((prev) => {
      const current = new Set(prev[itemNumber] || []);
      if (current.has(lp)) {
        current.delete(lp);
      } else {
        current.add(lp);
      }
      return { ...prev, [itemNumber]: current };
    });
  }, []);

  const handleReset = useCallback(() => {
    pickIdsRef.current = {};
    setSelections(emptySelections(materials));
    setQuantities({});
    setStep(STEP_SELECT);
  }, [materials]);

  const handleAdvanceToConfirm = useCallback(() => {
    setQuantities((prev) => {
      return defaultQuantities(materials, selections, prev);
    });
    setStep(STEP_CONFIRM);
  }, [materials, selections]);

  const handleAdjust = useCallback((itemNumber, lp, newQty) => {
    setQuantities((prev) => {
      return { ...prev, [`${itemNumber}:${lp}`]: newQty };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting || !hasSubmittableQuantity) return;

    const pickIdFor = (itemNumber, lp) => {
      const key = `${itemNumber}:${lp}`;
      if (!pickIdsRef.current[key]) {
        pickIdsRef.current[key] = uuidv4();
      }
      return pickIdsRef.current[key];
    };

    // Re-check each pick against the LP as it is now: a refresh while the
    // operator was on the confirm screen may have removed it or lowered what is
    // available. A pick this wizard already recorded (a retry) does not count
    // against its own LP.
    const problems = [];
    const picks = [];
    materials.forEach((mat) => {
      const sel = selections[mat.itemNumber] || new Set();
      sel.forEach((lp) => {
        const qty = quantities[`${mat.itemNumber}:${lp}`] ?? 0;
        if (qty > 0) {
          const lpInfo = mat.lps.find((l) => {
            return l.lp === lp;
          });
          if (!lpInfo) {
            problems.push(`${lp} is no longer available for this operation.`);
            return;
          }
          const ownId = pickIdsRef.current[`${mat.itemNumber}:${lp}`];
          const ownRecorded = recordedPicks
            .filter((pick) => {
              return ownId && pick.id === ownId && !pick.posted;
            })
            .reduce((sum, pick) => {
              return sum + pick.quantity;
            }, 0);
          const limit = (lpInfo.available ?? lpInfo.onHand) + ownRecorded;
          if (Number.isFinite(limit) && qty > limit) {
            const unit = mat.unit ? ` ${mat.unit}` : '';
            problems.push(
              `${lp} now has ${formatMaterialQty(
                limit
              )}${unit} available. Adjust the quantity and confirm again.`
            );
            return;
          }
          picks.push({
            id: pickIdFor(mat.itemNumber, lp),
            licensePlate: lp,
            location: lpInfo ? lpInfo.location : null,
            itemNumber: mat.itemNumber,
            quantity: qty,
            phase,
            ts: new Date().toISOString(),
            status: 'pending',
          });
        }
      });
    });

    if (problems.length) {
      problems.forEach((problem) => {
        throwErrorToast(problem);
      });
      return;
    }

    // At job start the job is not running yet: hold the picks until the start is
    // confirmed (useFlushStagedPicks writes them), so a failed start records no
    // material and a retry does not add a second set of picks. Staging is
    // synchronous, so there is no submitting state: backing out of activity
    // selection returns to a live Confirm button, and confirming again replaces
    // the staged set.
    if (mode === 'start-job') {
      // If this WOO is already running, that start is not the one these picks are
      // for: record it so the picks wait for the new start.
      const baselineStartRef =
        String(confirmedStart?.workOrderOperationRef) === String(wooId)
          ? confirmedStart.startRef
          : null;
      dispatch(
        actionStagePicks(
          wooId,
          picks.map((pick) => {
            return { ...pick, status: 'staged' };
          }),
          baselineStartRef
        )
      );
      onComplete();
      return;
    }

    setSubmitting(true);
    try {
      const { alreadyRecorded = [] } = (await submitPicks(wooId, picks)) || {};
      // An LP recorded by an earlier attempt keeps its recorded quantity: a changed
      // quantity is not written a second time.
      const recordedQty = new Map(
        alreadyRecorded.map(({ licensePlate, quantity }) => {
          return [licensePlate, quantity];
        })
      );
      picks.forEach((pick) => {
        dispatch(
          actionAddPick(
            wooId,
            recordedQty.has(pick.licensePlate)
              ? { ...pick, quantity: recordedQty.get(pick.licensePlate) }
              : pick
          )
        );
      });
      alreadyRecorded.forEach(({ licensePlate, quantity }) => {
        throwErrorToast(
          `${licensePlate} was already recorded with ${formatMaterialQty(
            quantity
          )}; the change was not applied.`
        );
      });
      dispatch(
        actionMarkPicksSubmitted(
          wooId,
          picks.map((pick) => {
            return pick.id;
          })
        )
      );
      pickIdsRef.current = {};
      throwSuccessToast('Material picks submitted');
      onComplete();
    } catch {
      throwErrorToast('Failed to submit picks.');
      setSubmitting(false);
    }
  }, [
    submitting,
    hasSubmittableQuantity,
    materials,
    selections,
    quantities,
    phase,
    mode,
    wooId,
    confirmedStart,
    recordedPicks,
    submitPicks,
    dispatch,
    onComplete,
  ]);

  // Backing out of a job start discards the picks staged for it.
  const discardStagedAnd = useCallback(
    (handler) => {
      if (!handler) return undefined;
      return () => {
        if (mode === 'start-job') {
          dispatch(actionClearStagedPicks(wooId));
        }
        return handler();
      };
    },
    [mode, wooId, dispatch]
  );

  if (step === STEP_CONFIRM) {
    return (
      <ConfirmQuantitiesStep
        materials={materials}
        selections={selections}
        quantities={quantities}
        onAdjust={handleAdjust}
        onReset={handleReset}
        onSubmit={handleSubmit}
        onBack={() => {
          return setStep(STEP_SELECT);
        }}
        mode={mode}
        submitting={submitting}
        canSubmit={hasSubmittableQuantity}
      />
    );
  }

  return (
    <SelectBatchStep
      materials={materials}
      selections={selections}
      onReset={handleReset}
      onConfirm={handleAdvanceToConfirm}
      onBack={discardStagedAnd(onBack)}
      onClose={discardStagedAnd(onClose)}
      manualSelectionEnabled={MANUAL_LP_SELECTION_ENABLED}
      loading={!loaded && !error}
      loadError={!loaded && Boolean(error)}
      onRetry={() => {
        return refetch();
      }}
      onToggleLp={handleToggleLp}
    />
  );
};

MaterialPickingWizard.propTypes = {
  // The WOO's workOrderOperationRef; picks are stored and submitted under it.
  wooId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  phase: PropTypes.oneOf(['setup', 'production']),
  mode: PropTypes.oneOf(['start-job', 'add-materials']),
  onComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
};

MaterialPickingWizard.defaultProps = {
  wooId: undefined,
  phase: 'production',
  mode: 'start-job',
  onBack: undefined,
  onClose: undefined,
};

export default MaterialPickingWizard;
